import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { AnnouncementService } from './announcement.service';
import { WEB_PUSH_PUBLIC_KEY } from '../constants/app.constants';

export interface PushCapabilityState {
  supported: boolean;
  permission: NotificationPermission;
  subscribed: boolean;
  isIosStandaloneRequired: boolean;
  errorMessage?: string;
}

@Injectable({ providedIn: 'root' })
export class PushNotificationService {
  private registration?: ServiceWorkerRegistration;
  private readonly capability$ = new BehaviorSubject<PushCapabilityState>({
    supported: false,
    permission: 'default',
    subscribed: false,
    isIosStandaloneRequired: false,
    errorMessage: undefined,
  });

  constructor(private announcements: AnnouncementService) {}

  stateChanges() {
    return this.capability$.asObservable();
  }

  private detectIosStandalone(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(userAgent);
    const standalone = (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) || (window.navigator as any).standalone;
    return isIos && !standalone;
  }

  async init(companyId: string, propertyId: string): Promise<void> {
    const serviceWorkerSupported = 'serviceWorker' in navigator;
    const isIosStandaloneRequired = this.detectIosStandalone();
    const permission = typeof Notification !== 'undefined' ? Notification.permission : 'default';
    let subscribed = false;
    let supported = false;
    let errorMessage: string | undefined;

    if (serviceWorkerSupported) {
      try {
        this.registration = await navigator.serviceWorker.register('/push-sw.js');
        supported = !!this.registration.pushManager;
        if (supported) {
          const existing = await this.registration.pushManager.getSubscription();
          subscribed = !!existing;
        } else {
          errorMessage = 'push-manager-unavailable';
        }
      } catch (error) {
        console.warn('service worker registration failed', error);
        supported = false;
        errorMessage = error instanceof Error ? error.message : `${error}`;
      }
    } else {
      errorMessage = 'service-worker-unsupported';
    }

    this.capability$.next({ supported, permission, subscribed, isIosStandaloneRequired, errorMessage });

    if (supported && subscribed && this.registration) {
      const subscription = await this.registration.pushManager.getSubscription();
      if (subscription) {
        await this.persistSubscription(companyId, propertyId, subscription);
      }
    }
  }

  async requestPermission(companyId: string, propertyId: string, language: string): Promise<boolean> {
    if (!this.registration || typeof Notification === 'undefined') {
      return false;
    }
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        this.capability$.next({ ...this.capability$.value, permission });
        return false;
      }
      const existing = await this.registration.pushManager.getSubscription();
      if (existing) {
        await this.persistSubscription(companyId, propertyId, existing, language);
        this.capability$.next({ ...this.capability$.value, permission: 'granted', subscribed: true });
        return true;
      }
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(WEB_PUSH_PUBLIC_KEY),
      });
      await this.persistSubscription(companyId, propertyId, subscription, language);
      this.capability$.next({ ...this.capability$.value, permission: 'granted', subscribed: true });
      return true;
    } catch (error) {
      console.error('Push subscription failed', error);
      this.capability$.next({ ...this.capability$.value, subscribed: false });
      throw error;
    }
  }

  async unsubscribe(companyId: string, propertyId: string): Promise<void> {
    if (!this.registration) return;
    const subscription = await this.registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      try {
        await firstValueFrom(this.announcements.unsubscribeFromPush(companyId, propertyId, subscription.endpoint));
      } catch (error) {
        console.warn('unsubscribe push failed', error);
      }
    }
    this.capability$.next({ ...this.capability$.value, subscribed: false });
  }

  private async persistSubscription(companyId: string, propertyId: string, subscription: PushSubscription, language?: string) {
    const payload = {
      endpoint: subscription.endpoint,
      keys: subscription.toJSON().keys,
      platform: window.navigator.userAgent,
      language,
    };
    try {
      await firstValueFrom(this.announcements.subscribeToPush(companyId, propertyId, payload));
    } catch (error) {
      console.warn('persist subscription failed', error);
    }
  }

  private urlBase64ToUint8Array(base64String: string | undefined) {
    if (!base64String) {
      throw new Error('Missing VAPID key');
    }
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}
